import type { PredictionMode } from '@/types/statistics';

export type InDepthTabKey = 'movement' | 'shots';

/**
 * Which In-Depth tabs are available for a prediction mode, in display order.
 * Basic reports carry no shot data, so the shots tab is dropped (leaving only
 * movement).
 *
 * Shots lead: they are what a player came to read. The FIRST key is also the
 * section's default tab, so this order decides what opens — which keeps basic
 * reports honest, since 'shots' isn't in their list to open on.
 */
export function inDepthTabKeys(
  predictionMode: PredictionMode,
): InDepthTabKey[] {
  const all: InDepthTabKey[] = ['shots', 'movement'];
  return predictionMode === 'advanced' ? all : all.filter((k) => k !== 'shots');
}

export interface InDepthTab {
  key: InDepthTabKey;
  /** The tab opens, but has nothing to show beyond what it would contain. */
  locked: boolean;
}

/**
 * The tabs to DISPLAY, which is not the same list as the ones with content.
 *
 * A basic report shows the shots tab too, locked: it opens onto a description
 * of what the tab holds and a way to get it, rather than being absent. Hiding
 * it entirely is why a basic reader never learns the analysis exists.
 *
 * Order is the same in both modes, and the default tab still comes from
 * `inDepthTabKeys`, so a basic report opens on movement rather than landing
 * the reader on a locked tab.
 */
export function inDepthTabsShown(
  predictionMode: PredictionMode,
): InDepthTab[] {
  const unlocked = new Set(inDepthTabKeys(predictionMode));
  return (['shots', 'movement'] as InDepthTabKey[]).map((key) => ({
    key,
    locked: !unlocked.has(key),
  }));
}
